// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  clearAuthCookies,
  clearCookies,
  clearCookiesAndStorage,
  getUserData,
  setSessionToken,
} from '@/context/auth-context/auth-utils';
import { API_BASE } from '@/config';
import { AuthContext } from '@/context/auth-context/auth-context';
import type { SessionUser } from '@/types/user';
import { setCurrentUser } from '@/stores/auth-store.ts';
import { useNavigate } from 'react-router-dom';
import { IS_PUBLIC_ROUTE } from '@/public-routes.ts';

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
                                                                  children,
                                                                }) => {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [userUpdatedAt, setUserUpdatedAt] = useState<number>(Date.now());
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const hasCheckedSession = useRef(false);

  const checkSession = useCallback(
    async (force = false) => {
      if (hasCheckedSession.current && !force) return;

      setIsLoading(true);
      try {
        const userData = getUserData();

        if (userData && !force) { // only if no force check -> load from cookies
          setUser(userData);        /// The issue is when user changes AVATAR -> in call its old
        } else {
          const response = await fetch(`${API_BASE}/api/auth/session`, {
            credentials: 'include',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${sessionStorage.getItem('session_token')}`,
            },
          });

          const contentType = response.headers.get('content-type');
          if (!contentType?.includes('application/json')) {
            throw new Error(`Expected JSON but got ${contentType}`);
          }

          const data = await response.json();

          if (response.ok && data.success) {
            setUser(data.user);
            setSessionToken(data.sessionToken);

          } else {
            clearAuthCookies();
            setUser(null);
            if (!IS_PUBLIC_ROUTE) {
              navigate('/auth/login', { replace: true });
            }
          }
        }
      } catch (error) {
        clearAuthCookies();
        setUser(null);

        if (!IS_PUBLIC_ROUTE) {
          navigate('/auth/login', { replace: true });
        }
      } finally {
        setIsLoading(false);
        hasCheckedSession.current = true;
      }
    },
    [navigate]
  );

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      clearAuthCookies();
      setCurrentUser(null);
      setUser(null);
      hasCheckedSession.current = false;
    }
  };

  const deleteCookiesAndSession = async () => {
    try {
      clearCookiesAndStorage();
    } catch (error) {
      console.error('Delete  error:', error);
    }
  };

  const updateUser = (newUser: SessionUser | null) => {
    setUser(newUser);
    setUserUpdatedAt(Date.now());
  };

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser: updateUser,
        logout,
        checkSession,
        isLoading,
        userUpdatedAt,
        deleteCookiesAndSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

